/**
 * The eleven stage scenes: each dashboard's data as objects on the floor in front of its
 * frame. Pure functions of the dashboard's view and filters, so they are unit testable.
 */
import type { DashboardFilters, SubjectStatus, Visit } from '@clinspace/shared';
import { KRI_IDS, QUERY_AGE_BUCKETS, SUBJECT_STATUSES, queryAgeDays } from '@clinspace/shared';
import { PALETTE, SERIES, squarify } from './charts.ts';
import type { Dashboard } from './Dashboard.ts';
import {
  STAGE,
  type StageBox,
  type StageItem,
  type StageNote,
  type StageScene,
} from './StageLayer.ts';
import { StudyView } from './StudyView.ts';
import { STAGE_GAP } from './impl/DataCleaning.ts';

const W = STAGE.xMax - STAGE.xMin;
const D = STAGE.zMax - STAGE.zMin;
const zMid = (STAGE.zMin + STAGE.zMax) / 2;
const H = STAGE.yMax;

const STATUS_COLOURS: Record<SubjectStatus, string> = {
  Screening: PALETTE.grey,
  'Screen Failed': '#5b6470',
  Enrolled: PALETTE.navy,
  'On Treatment': PALETTE.teal,
  Completed: PALETTE.forest,
  Discontinued: PALETTE.amber,
  'Lost to Follow-up': PALETTE.crimson,
};
const TIMING: Record<Visit['timing'], { h: number; colour: string }> = {
  'On time': { h: 0.03, colour: PALETTE.teal },
  Early: { h: 0.08, colour: PALETTE.violet },
  Late: { h: 0.125, colour: PALETTE.amber },
  Missed: { h: 0.24, colour: PALETTE.crimson },
  Pending: { h: 0.012, colour: '#2c3a4d' },
};

/** The top of an object, where a callout's leader line starts. */
function topOf(item: StageItem): number {
  if (item.kind === 'sphere') return item.y + item.r;
  return item.y + item.h;
}

/** A callout over an object: the metric that matters most on this stage, always shown. */
function callout(item: StageItem, title: string, detail: string, colour: string): StageNote {
  return { kind: 'callout', x: item.x, y: topOf(item), z: item.z, title, detail, colour };
}

export function buildStage(d: Dashboard): StageScene | undefined {
  const view = d.view;
  const f = d.filters;
  switch (d.id) {
    case 'Enrollment':
      return enrollment(view, f);
    case 'SubjectStatus':
      return subjectStatus(view, f);
    case 'VisitCompliance':
      return visitCompliance(view, f);
    case 'MissingData':
      return missingData(view, f);
    case 'QueryManagement':
      return queryManagement(view, f);
    case 'DataCleaning':
      return dataCleaning(view, f);
    case 'ProtocolDeviation':
      return protocolDeviation(view, f);
    case 'LabAbnormality':
      return labAbnormality(view, f);
    case 'SAEReview':
      return saeReview(view, f);
    case 'RiskBasedMonitoring':
      return rbm(view, f);
    case 'PatientProfile':
      return patientProfiles(view, f);
  }
}

/** Enrollment: a ramp of weekly cumulative enrollment climbing toward the target, planned as glass. */
function enrollment(view: StudyView, f: DashboardFilters): StageScene {
  const e = view.file.dashboards.enrollment;
  const target = Math.max(1, e.kpis.target);
  const ids = view.subjectIds(f);
  const filtered = StudyView.isFiltered(f);
  const n = e.cumulative.length;
  const slot = W / Math.max(1, n);
  const items: StageItem[] = [];
  const glass: StageScene['glass'] = [];
  e.cumulative.forEach((p, i) => {
    const actual = filtered
      ? view.file.subjects.filter(
          (s) => ids.has(s.id) && s.randomisationDate && s.randomisationDate <= p.weekEnding,
        ).length
      : p.actual;
    const x = STAGE.xMin + slot * (i + 0.5);
    const h = 0.04 + (actual / target) * (H - 0.06);
    items.push({
      kind: 'box',
      x,
      y: 0,
      z: zMid,
      w: slot * 0.85,
      h,
      d: 0.55,
      colour: i === n - 1 ? '#5fd3ce' : PALETTE.teal,
      tooltip: [
        `Week ending ${p.weekEnding}`,
        `${actual} enrolled · ${p.planned} planned`,
        `${Math.round((actual / target) * 100)}% of the target of ${target}`,
      ],
    });
    glass.push({
      kind: 'glass',
      x,
      y: 0.04 + (Math.min(p.planned, target) / target) * (H - 0.06),
      z: zMid,
      w: slot * 0.85,
      h: 0.012,
      d: 0.58,
      colour: PALETTE.amber,
    });
  });
  const enrolled = filtered
    ? view.subjects(f).filter((s) => s.randomisationDate).length
    : e.kpis.enrolled;
  const lastWeek = items[items.length - 1];
  const planned = e.cumulative[n - 1]?.planned ?? 0;
  const notes: StageNote[] = lastWeek
    ? [
        callout(
          lastWeek,
          `${enrolled} of ${target} enrolled (${Math.round((enrolled / target) * 100)}%)`,
          `${Math.round(planned)} planned by now · ${enrolled >= planned ? 'on plan' : `${Math.round(planned - enrolled)} behind`}`,
          enrolled >= planned ? PALETTE.teal : PALETTE.amber,
        ),
      ]
    : [];
  return {
    items,
    glass,
    notes,
    label: {
      title: 'Cumulative enrollment: a ramp toward the target',
      legend: [
        { label: 'Actual', colour: PALETTE.teal },
        { label: 'Planned (glass line)', colour: PALETTE.amber },
      ],
      caption: `${enrolled} of ${target} enrolled · each step is one week · the last step is this week`,
      xLabels: [
        { x: STAGE.xMin + slot * 0.5, text: e.cumulative[0]?.weekEnding.slice(0, 7) ?? '' },
        { x: STAGE.xMax - slot * 0.5, text: e.cumulative[n - 1]?.weekEnding.slice(0, 7) ?? '' },
      ],
    },
  };
}

/** Subject Status: a small person per subject, standing in rows in a column per status. */
function subjectStatus(view: StudyView, f: DashboardFilters): StageScene {
  const subjects = view.subjects(f);
  const shown = SUBJECT_STATUSES.filter((s) => s !== 'Enrolled');
  const slot = W / shown.length;
  const items: StageItem[] = [];
  shown.forEach((status, si) => {
    const cx = STAGE.xMin + slot * (si + 0.5);
    const mine = subjects.filter((s) => s.status === status);
    const step = Math.min(0.2, (D - 0.2) / Math.max(1, Math.ceil(mine.length / 3)));
    mine.forEach((s, k) => {
      const col = k % 3;
      const row = Math.floor(k / 3);
      items.push({
        kind: 'figure',
        sex: s.sex,
        x: cx + (col - 1) * 0.1,
        y: 0,
        z: STAGE.zMax - 0.12 - row * step,
        h: 0.2,
        colour: STATUS_COLOURS[status],
        tooltip: [
          `${s.id} · ${s.sex === 'F' ? 'female' : 'male'}, ${s.age}`,
          `${status} since ${s.statusDate}`,
          `${view.siteLabel(s.siteId)}${s.arm ? ` · ${s.arm}` : ''}`,
          'Select to open the timeline',
        ],
        select: { kind: 'drill', path: [`status:${status}`, `subject:${s.id}`] },
      });
    });
  });
  const notes: StageNote[] = [];
  const stopped = subjects.filter(
    (x) => x.status === 'Discontinued' || x.status === 'Lost to Follow-up',
  );
  const firstStopped = items.find(
    (i) =>
      i.colour === STATUS_COLOURS.Discontinued || i.colour === STATUS_COLOURS['Lost to Follow-up'],
  );
  if (firstStopped && stopped.length)
    notes.push(
      callout(
        firstStopped,
        `${stopped.length} stopped early`,
        `${subjects.filter((x) => x.status === 'Discontinued').length} discontinued · ${subjects.filter((x) => x.status === 'Lost to Follow-up').length} lost to follow-up`,
        PALETTE.amber,
      ),
    );
  return {
    items,
    notes,
    label: {
      title: 'Subjects by status, one person each',
      legend: shown.map((s) => ({ label: s, colour: STATUS_COLOURS[s] })),
      caption: `${subjects.length} subjects · front row nearest the table · select a person to open that subject`,
      xLabels: shown.map((s, i) => ({
        x: STAGE.xMin + slot * (i + 0.5),
        text: `${subjects.filter((x) => x.status === s).length}`,
      })),
    },
  };
}

/** Visit Compliance: a carpet, subjects across, visits in depth, tiles rise with lateness. */
function visitCompliance(view: StudyView, f: DashboardFilters): StageScene {
  const subjects = view.subjects(f).filter((s) => s.firstDoseDate);
  const schedule = view.file.study.visitSchedule;
  const slotX = W / Math.max(1, subjects.length);
  const slotZ = D / Math.max(1, schedule.length);
  const items: StageItem[] = [];
  subjects.forEach((s, si) => {
    const visits = view.file.visits.filter((v) => v.subjectId === s.id);
    for (const v of visits) {
      const vi = view.visitIndex(v.visitCode);
      if (vi < 0 || v.status === 'Not Applicable') continue;
      const t = TIMING[v.timing];
      items.push({
        kind: 'box',
        x: STAGE.xMin + slotX * (si + 0.5),
        y: 0,
        z: STAGE.zMax - slotZ * (vi + 0.5),
        w: slotX * 0.78,
        h: t.h,
        d: slotZ * 0.78,
        colour: t.colour,
        tooltip: [
          `${s.id} · ${v.visitName}`,
          v.actualDate
            ? `${v.actualDate} · ${v.timing}${v.deviationDays ? ` (${v.deviationDays} d)` : ''}`
            : v.status,
          'Select to open the visit',
        ],
        select: { kind: 'drill', path: [`subject:${s.id}`, `visit:${v.id}`] },
      });
    }
  });
  const missed = items.filter((i) => i.colour === TIMING.Missed.colour);
  const bySubject = new Map<string, number>();
  for (const m of missed) {
    const id = m.tooltip[0]?.split(' · ')[0] ?? '';
    bySubject.set(id, (bySubject.get(id) ?? 0) + 1);
  }
  const worst = [...bySubject.entries()].sort((a, b) => b[1] - a[1])[0];
  const worstTile = worst ? missed.find((m) => m.tooltip[0]?.startsWith(worst[0])) : undefined;
  const notes: StageNote[] =
    worst && worstTile
      ? [
          callout(
            worstTile,
            `${missed.length} missed visit${missed.length === 1 ? '' : 's'}`,
            `most for ${worst[0]} (${worst[1]})`,
            PALETTE.crimson,
          ),
        ]
      : [];
  return {
    items,
    notes,
    label: {
      title: 'Visit compliance carpet: taller tiles are later',
      legend: [
        { label: 'On time', colour: PALETTE.teal },
        { label: 'Early', colour: PALETTE.violet },
        { label: 'Late', colour: PALETTE.amber },
        { label: 'Missed', colour: PALETTE.crimson },
        { label: 'Pending', colour: '#2c3a4d' },
      ],
      caption: `${subjects.length} dosed subjects across · screening at the front, follow-up at the back`,
    },
  };
}

/** Missing Data: a column field, visits across, forms in depth, height is missing %. */
function missingData(view: StudyView, f: DashboardFilters): StageScene {
  const forms = view.forms(f);
  const visits = view.file.study.visitSchedule.map((v) => v.code);
  const names = view.file.study.forms.map((x) => x.name);
  const slotX = W / visits.length;
  const slotZ = D / names.length;
  const items: StageItem[] = [];
  names.forEach((name, r) => {
    visits.forEach((code, c) => {
      const cell = forms.filter((x) => x.visitCode === code && x.form === name);
      if (!cell.length) return;
      const exp = cell.reduce((s, x) => s + x.totalFields, 0);
      const miss = cell.reduce(
        (s, x) => s + (x.status === 'Not Started' ? x.totalFields : x.missingFields.length),
        0,
      );
      const pct = exp ? Math.round((miss / exp) * 1000) / 10 : 0;
      const h = 0.025 + Math.min(1, pct / 25) * (H - 0.05);
      items.push({
        kind: 'box',
        x: STAGE.xMin + slotX * (c + 0.5),
        y: 0,
        z: STAGE.zMax - slotZ * (r + 0.5),
        w: slotX * 0.7,
        h,
        d: slotZ * 0.7,
        colour:
          pct > 10 ? PALETTE.crimson : pct > 5 ? PALETTE.amber : pct > 0 ? PALETTE.teal : '#2c3a4d',
        tooltip: [
          `${name} at ${code}`,
          `${pct}% missing (${miss} of ${exp} fields, ${cell.length} subjects)`,
          miss ? 'Select to open the pages' : 'Complete',
        ],
        select: miss ? { kind: 'drill', path: [`cell:${code}|${name}`] } : undefined,
      });
    });
  });
  const emptiest = [...items].sort((a, b) => topOf(b) - topOf(a))[0];
  const notes: StageNote[] =
    emptiest && emptiest.colour !== '#2c3a4d'
      ? [
          callout(
            emptiest,
            `Most missing: ${emptiest.tooltip[0]}`,
            emptiest.tooltip[1] ?? '',
            emptiest.colour === PALETTE.crimson ? PALETTE.crimson : PALETTE.amber,
          ),
        ]
      : [];
  return {
    items,
    notes,
    label: {
      title: 'Missing data field: taller columns are emptier pages',
      legend: [
        { label: 'Over 10%', colour: PALETTE.crimson },
        { label: '5 to 10%', colour: PALETTE.amber },
        { label: 'Under 5%', colour: PALETTE.teal },
        { label: 'Complete', colour: '#2c3a4d' },
      ],
      caption: 'visits across (screening left) · forms in depth (demographics at the back)',
      xLabels: visits.map((v, c) => ({ x: STAGE.xMin + slotX * (c + 0.5), text: v })),
    },
  };
}

/** Query Management: four towers of cards, one per age bucket, each card an unresolved query. */
function queryManagement(view: StudyView, f: DashboardFilters): StageScene {
  const open = view
    .queriesFiltered(f)
    .filter((q) => q.status === 'Open' || q.status === 'Answered');
  const slot = W / QUERY_AGE_BUCKETS.length;
  const items: StageItem[] = [];
  QUERY_AGE_BUCKETS.forEach((b, bi) => {
    const cx = STAGE.xMin + slot * (bi + 0.5);
    const mine = open
      .filter((q) => {
        const age = queryAgeDays(q);
        return (age <= 7 ? '0-7' : age <= 14 ? '8-14' : age <= 30 ? '15-30' : '30+') === b;
      })
      .sort((a, c) => a.raisedAt.localeCompare(c.raisedAt));
    mine.forEach((q, k) => {
      items.push({
        kind: 'box',
        x: cx + (((k * 37) % 7) - 3) * 0.012,
        y: Math.min(k, 18) * 0.038,
        z: zMid,
        w: 0.42,
        h: 0.03,
        d: 0.28,
        rotY: (((k * 53) % 9) - 4) * 0.03,
        colour:
          q.status === 'Answered'
            ? PALETTE.teal
            : q.priority === 'High'
              ? PALETTE.crimson
              : PALETTE.amber,
        tooltip: [
          `${q.subjectId} · ${q.form}${q.field ? ` · ${q.field}` : ''}`,
          q.text.length > 70 ? `${q.text.slice(0, 67)}…` : q.text,
          `${q.status} · ${q.priority} · ${queryAgeDays(q)} days · ${q.raisedBy.name}`,
          'Select to open the query',
        ],
        select: { kind: 'drill', path: [`age:${b}`, `query:${q.id}`] },
      });
    });
  });
  const notes: StageNote[] = [];
  const oldest = open
    .filter((q) => queryAgeDays(q) > 30)
    .sort((a, b) => queryAgeDays(b) - queryAgeDays(a));
  const tower = items.filter((i) => i.tooltip[3] && (i as StageBox).x > STAGE.xMin + slot * 3);
  const towerTop = tower.sort((a, b) => topOf(b) - topOf(a))[0];
  if (oldest.length && towerTop)
    notes.push(
      callout(
        towerTop,
        `${oldest.length} unresolved for over 30 days`,
        `oldest ${queryAgeDays(oldest[0]!)} days: ${oldest[0]!.subjectId} · ${oldest[0]!.form}`,
        PALETTE.crimson,
      ),
    );
  return {
    items,
    notes,
    label: {
      title: 'Query towers: unresolved queries stacked by age',
      legend: [
        { label: 'Open', colour: PALETTE.amber },
        { label: 'Open, high priority', colour: PALETTE.crimson },
        { label: 'Answered, not closed', colour: PALETTE.teal },
      ],
      caption: `${open.length} unresolved · the tallest tower is where the backlog is`,
      xLabels: QUERY_AGE_BUCKETS.map((b, i) => ({
        x: STAGE.xMin + slot * (i + 0.5),
        text: `${b} days`,
      })),
    },
  };
}

/** Data Cleaning: a funnel of discs, one per cleaning stage, plus a column per form needing attention. */
function dataCleaning(view: StudyView, f: DashboardFilters): StageScene {
  const forms = view.forms(f);
  const rank = { 'Not Started': 0, Entered: 1, Verified: 2, Signed: 3, Locked: 4 } as const;
  const stages: { label: string; count: number; colour: string }[] = [
    { label: 'Expected', count: forms.length, colour: PALETTE.grey },
    {
      label: 'Entered',
      count: forms.filter((x) => rank[x.status] >= 1).length,
      colour: PALETTE.navy,
    },
    {
      label: 'Verified',
      count: forms.filter((x) => rank[x.status] >= 2).length,
      colour: PALETTE.teal,
    },
    {
      label: 'Signed',
      count: forms.filter((x) => rank[x.status] >= 3).length,
      colour: PALETTE.forest,
    },
    {
      label: 'Locked',
      count: forms.filter((x) => x.status === 'Locked').length,
      colour: PALETTE.violet,
    },
  ];
  const total = Math.max(1, forms.length);
  const items: StageItem[] = stages.map((s, i) => ({
    kind: 'cylinder',
    x: -0.45,
    y: i * 0.14,
    z: zMid,
    r: 0.12 + Math.sqrt(s.count / total) * 0.5,
    h: 0.125,
    colour: s.colour,
    tooltip: [
      s.label,
      `${s.count} of ${total} pages (${Math.round((s.count / total) * 100)}%)`,
      STAGE_GAP[s.label]?.hint ?? '',
    ],
    select: { kind: 'drill', path: [`stage:${s.label}`] },
  }));
  const names = view.file.study.forms.map((x) => x.name);
  const slot = 1.0 / Math.max(1, names.length);
  names.forEach((name, i) => {
    const mine = forms.filter((x) => x.form === name);
    const attention = mine.filter(
      (x) => x.status === 'Not Started' || (x.status === 'Entered' && x.missingFields.length > 0),
    ).length;
    items.push({
      kind: 'box',
      x: 0.2 + slot * (i + 0.5),
      y: 0,
      z: zMid,
      w: slot * 0.7,
      h: 0.03 + (attention / Math.max(1, mine.length)) * (H - 0.05),
      d: 0.5,
      colour: attention ? PALETTE.amber : PALETTE.teal,
      tooltip: [
        name,
        `${attention} of ${mine.length} pages need attention`,
        'Select to open the list',
      ],
      select: { kind: 'drill', path: [`form:${name}`] },
    });
  });
  const formBars = items.filter((i) => i.kind === 'box');
  const neediest = [...formBars].sort((a, b) => topOf(b) - topOf(a))[0];
  const notes: StageNote[] =
    neediest && neediest.colour === PALETTE.amber
      ? [
          callout(
            neediest,
            `Needs attention: ${neediest.tooltip[0]}`,
            neediest.tooltip[1] ?? '',
            PALETTE.amber,
          ),
        ]
      : [];
  return {
    items,
    notes,
    label: {
      title: 'Cleaning funnel (left) and attention per form (right)',
      legend: stages
        .map((s) => ({ label: s.label, colour: s.colour }))
        .concat([{ label: 'Pages needing attention', colour: PALETTE.amber }]),
      caption: `${stages[4]!.count} of ${forms.length} pages locked · disc width is the share of pages at that stage`,
      xLabels: names.map((n, i) => ({ x: 0.2 + slot * (i + 0.5), text: n.split(' ')[0]! })),
    },
  };
}

/** Protocol Deviation: a treemap of tiles, area by count, height by major deviations. */
function protocolDeviation(view: StudyView, f: DashboardFilters): StageScene {
  const devs = view.deviations(f);
  const cats = [...new Set(devs.map((x) => x.category))];
  const cells = squarify(
    cats.map((c, i) => ({
      label: c,
      value: devs.filter((x) => x.category === c).length,
      colour: SERIES[i % SERIES.length]!,
    })),
    { x: STAGE.xMin, y: STAGE.zMin, w: W, h: D },
  );
  const items: StageItem[] = cells.map((cell) => {
    const mine = devs.filter((x) => x.category === cell.item.label);
    const major = mine.filter((x) => x.classification === 'Major').length;
    const unclassified = mine.filter((x) => x.classification === null).length;
    return {
      kind: 'box',
      x: cell.x + cell.w / 2,
      y: 0,
      z: cell.y + cell.h / 2,
      w: Math.max(0.05, cell.w - 0.05),
      h: Math.min(H, 0.05 + major * 0.12 + unclassified * 0.06),
      d: Math.max(0.05, cell.h - 0.05),
      colour: unclassified ? PALETTE.amber : cell.item.colour,
      tooltip: [
        cell.item.label,
        `${mine.length} deviations · ${major} major · ${unclassified} unclassified`,
        'Select to open the list',
      ],
      select: { kind: 'drill', path: [`category:${cell.item.label}`] },
    } as StageBox;
  });
  const notes: StageNote[] = [];
  const majors = devs.filter((x) => x.classification === 'Major').length;
  const tallest = [...items].sort((a, b) => topOf(b) - topOf(a))[0];
  if (majors && tallest)
    notes.push(
      callout(
        tallest,
        `${majors} major deviation${majors === 1 ? '' : 's'}`,
        `most in ${tallest.tooltip[0]}`,
        PALETTE.crimson,
      ),
    );
  const unclassified = devs.filter((x) => x.classification === null).length;
  const amberTile = items.find((i) => i.colour === PALETTE.amber && i !== tallest);
  if (unclassified && amberTile)
    notes.push(
      callout(
        amberTile,
        `${unclassified} still unclassified`,
        'needs a major or minor decision',
        PALETTE.amber,
      ),
    );
  return {
    items,
    notes,
    label: {
      title: 'Deviation treemap: area is count, height is major deviations',
      legend: cells.map((c) => ({ label: c.item.label, colour: c.item.colour ?? PALETTE.grey })),
      caption: `${devs.length} deviations · amber tiles still need classification`,
    },
  };
}

/** Lab Abnormality: the scatter cube. x ALT, height bilirubin, depth visit; glass walls at Hy's law thresholds. */
function labAbnormality(view: StudyView, f: DashboardFilters): StageScene {
  const d = view.file.dashboards.labAbnormality;
  const ids = view.subjectIds(f);
  const points = d.scatter.filter((p) => ids.has(p.subjectId));
  const nVisits = Math.max(2, view.file.study.visitSchedule.length);
  const items: StageItem[] = points.map((p) => {
    const vi = Math.max(0, view.visitIndex(p.visitCode));
    return {
      kind: 'sphere',
      x: STAGE.xMin + Math.min(1, p.altUln / 5) * W,
      y: 0.06 + Math.min(1, p.biliUln / 3) * (H - 0.1),
      z: STAGE.zMax - (vi / (nVisits - 1)) * D,
      r: p.hysLaw ? 0.055 : p.altUln > 3 || p.biliUln > 1.5 ? 0.04 : 0.028,
      colour: p.hysLaw
        ? PALETTE.crimson
        : p.altUln > 3 || p.biliUln > 1.5
          ? PALETTE.amber
          : PALETTE.teal,
      tooltip: [
        `${p.subjectId} · ${p.visitCode}`,
        `ALT ${p.altUln}× ULN · bilirubin ${p.biliUln}× ULN${p.hysLaw ? " · Hy's law" : ''}`,
        'Select to open the lab series',
      ],
      select: { kind: 'drill', path: [`subject:${p.subjectId}`] },
    };
  });
  const xAlt3 = STAGE.xMin + (3 / 5) * W;
  const yBili2 = 0.06 + (2 / 3) * (H - 0.1);
  // The axes say what they measure, in the cube itself (2026-10-05, the user's request).
  const notes: StageNote[] = [
    {
      kind: 'axis',
      x: STAGE.xMax - 0.2,
      y: 0.05,
      z: STAGE.zMax + 0.08,
      title: 'ALT × ULN →',
      colour: PALETTE.textSoft,
    },
    {
      kind: 'axis',
      x: STAGE.xMin - 0.02,
      y: H + 0.06,
      z: STAGE.zMax,
      title: '↑ Bilirubin × ULN',
      colour: PALETTE.textSoft,
    },
    {
      kind: 'axis',
      x: STAGE.xMin + 0.2,
      y: 0.05,
      z: STAGE.zMin,
      title: 'later visits ↗ back',
      colour: PALETTE.textSoft,
    },
  ];
  // Every Hy's law candidate, once per subject (their highest point).
  const seen = new Set<string>();
  points
    .map((p, i) => ({ p, item: items[i]! }))
    .filter(({ p }) => p.hysLaw)
    .sort((a, b) => b.p.biliUln - a.p.biliUln)
    .forEach(({ p, item }) => {
      if (seen.has(p.subjectId)) return;
      seen.add(p.subjectId);
      notes.push(
        callout(
          item,
          `Hy's law candidate · ${p.subjectId}`,
          `ALT ${p.altUln}× · bilirubin ${p.biliUln}× ULN · ${p.visitCode}`,
          PALETTE.crimson,
        ),
      );
    });
  return {
    items,
    notes,
    glass: [
      { kind: 'glass', x: xAlt3, y: 0, z: zMid, w: 0.012, h: H, d: D, colour: PALETTE.amber },
      { kind: 'glass', x: 0, y: yBili2, z: zMid, w: W, h: 0.012, d: D, colour: PALETTE.crimson },
    ],
    lines: [
      [STAGE.xMin, 0.01, STAGE.zMax, STAGE.xMax, 0.01, STAGE.zMax],
      [STAGE.xMin, 0.01, STAGE.zMax, STAGE.xMin, H, STAGE.zMax],
      [STAGE.xMin, 0.01, STAGE.zMax, STAGE.xMin, 0.01, STAGE.zMin],
    ],
    label: {
      title: "Liver scatter cube: Hy's law corner is top right",
      legend: [
        { label: "Hy's law candidate", colour: PALETTE.crimson },
        { label: 'Elevated', colour: PALETTE.amber },
        { label: 'Within range', colour: PALETTE.teal },
      ],
      caption:
        'across: ALT 0 to 5× ULN (amber wall at 3×) · height: bilirubin 0 to 3× (red floor at 2×) · depth: later visits further back',
      xLabels: [
        { x: STAGE.xMin + 0.05, text: 'ALT 0×' },
        { x: xAlt3, text: '3×' },
        { x: STAGE.xMax - 0.05, text: '5×' },
      ],
    },
  };
}

/** SAE Review: a timeline wall, cards hang at onset day, higher is more severe. */
function saeReview(view: StudyView, f: DashboardFilters): StageScene {
  const timeline = view.file.dashboards.saeReview.timeline;
  const ids = view.subjectIds(f);
  const studyDays = Math.max(
    1,
    Math.round(
      (Date.parse(view.file.anchorDate) - Date.parse(view.file.study.startDate)) / 86_400_000,
    ),
  );
  const items: StageItem[] = timeline
    .filter((t) => ids.has(t.subjectId))
    .map((t, i) => {
      const ae = view.ae(t.aeId);
      const grade = ae?.grade ?? 3;
      const colour = t.criteria.includes('Death')
        ? '#8b1020'
        : t.criteria.includes('Life-threatening')
          ? PALETTE.crimson
          : t.criteria.includes('Disability')
            ? PALETTE.violet
            : PALETTE.amber;
      return {
        kind: 'box',
        x: STAGE.xMin + (Math.max(0, t.studyDayFromStart) / studyDays) * W,
        y: 0.06 + (grade - 3) * 0.27,
        z: zMid + ((i % 3) - 1) * 0.2,
        w: 0.16,
        h: 0.11,
        d: 0.02,
        colour,
        tooltip: [
          `${ae?.term ?? t.aeId} · ${t.subjectId}`,
          `${ae?.onsetDate ?? ''} · grade ${grade} · ${ae?.relatedness ?? ''}${ae && !ae.expected ? ' · unexpected' : ''}`,
          t.criteria.join(', '),
          'Select to open the narrative',
        ],
        select: { kind: 'drill', path: [`sae:${t.aeId}`] },
      };
    });
  const shown = timeline.filter((t) => ids.has(t.subjectId));
  const ranked = shown
    .map((t, i) => ({ t, item: items[i]!, ae: view.ae(t.aeId) }))
    .filter(({ t, ae }) => t.criteria.includes('Death') || (ae && !ae.expected))
    .sort((a, b) => Number(b.t.criteria.includes('Death')) - Number(a.t.criteria.includes('Death')))
    .slice(0, 2);
  const notes: StageNote[] = ranked.map(({ t, item, ae }) =>
    callout(
      item,
      `${t.criteria.includes('Death') ? 'Fatal' : 'Unexpected'} SAE: ${ae?.term ?? t.aeId}`,
      `${t.subjectId} · grade ${ae?.grade ?? '?'} · ${ae?.onsetDate ?? ''}`,
      t.criteria.includes('Death') ? '#ff5d73' : PALETTE.crimson,
    ),
  );
  return {
    items,
    notes,
    lines: [[STAGE.xMin, 0.02, zMid, STAGE.xMax, 0.02, zMid]],
    label: {
      title: 'SAE timeline wall: left to right is study time, height is grade',
      legend: [
        { label: 'Hospitalisation', colour: PALETTE.amber },
        { label: 'Life-threatening', colour: PALETTE.crimson },
        { label: 'Death', colour: '#8b1020' },
        { label: 'Disability', colour: PALETTE.violet },
      ],
      caption: `${items.length} serious adverse events · grade 3 low, grade 5 high`,
      xLabels: [
        { x: STAGE.xMin + 0.05, text: view.file.study.startDate },
        { x: STAGE.xMax - 0.05, text: view.file.anchorDate },
      ],
    },
  };
}

/** RBM: a post per site on the map, height is risk score, ring is KRIs breached. */
function rbm(view: StudyView, f: DashboardFilters): StageScene {
  const sites = view.file.rbm.sites.filter((s) => !f.siteId || s.siteId === f.siteId);
  const rag = { Green: PALETTE.forest, Amber: PALETTE.amber, Red: PALETTE.crimson };
  const items: StageItem[] = [];
  for (const s of sites) {
    const site = view.site(s.siteId);
    const x = STAGE.xMin + (site?.mapX ?? 0.5) * W;
    const z = STAGE.zMin + (site?.mapY ?? 0.5) * D;
    const h = 0.08 + (s.score / 100) * (H - 0.14);
    const tooltip = [
      view.siteLabel(s.siteId),
      `Risk ${s.score} (${s.rag}) · ${s.krisBreached} KRIs breached · trend ${s.trend > 0 ? '+' : ''}${s.trend}`,
      KRI_IDS.filter((k) => s.kris[k].breached)
        .map((k) => k)
        .join(', ') || 'No KRI breached',
      'Select to open the site',
    ];
    items.push({
      kind: 'cylinder',
      x,
      y: 0,
      z,
      r: 0.11,
      h,
      colour: rag[s.rag],
      tooltip,
      select: { kind: 'drill', path: [`site:${s.siteId}`] },
    });
    if (s.krisBreached)
      items.push({
        kind: 'cylinder',
        x,
        y: h + 0.01,
        z,
        r: 0.14 + s.krisBreached * 0.03,
        h: 0.025,
        colour: PALETTE.amber,
        tooltip,
        select: { kind: 'drill', path: [`site:${s.siteId}`] },
      });
  }
  const notes: StageNote[] = [...sites]
    .filter((x) => x.rag === 'Red')
    .sort((a, b) => b.score - a.score)
    .slice(0, 2)
    .flatMap((x) => {
      const post = items.find(
        (i) => i.tooltip[0] === view.siteLabel(x.siteId) && i.colour === rag.Red,
      );
      if (!post) return [];
      return [
        callout(
          post,
          `High risk: ${view.site(x.siteId)?.city ?? x.siteId}`,
          `score ${x.score} · ${x.krisBreached} KRI${x.krisBreached === 1 ? '' : 's'} breached`,
          PALETTE.crimson,
        ),
      ];
    });
  return {
    items,
    notes,
    label: {
      title: 'Site risk map: posts rise with the risk score',
      legend: [
        { label: 'Red', colour: PALETTE.crimson },
        { label: 'Amber', colour: PALETTE.amber },
        { label: 'Green', colour: PALETTE.forest },
        { label: 'Ring: KRIs breached', colour: PALETTE.amber },
      ],
      caption: 'laid out as on the map · a wider ring means more KRIs breached',
      xLabels: sites.map((s) => ({
        x: STAGE.xMin + (view.site(s.siteId)?.mapX ?? 0.5) * W,
        text: view.site(s.siteId)?.city ?? s.siteId,
      })),
    },
  };
}

/** Patient Profiles: a small person per subject in a column per site, coloured by flags. */
function patientProfiles(view: StudyView, f: DashboardFilters): StageScene {
  const ids = view.subjectIds(f);
  const cards = view.file.dashboards.patientProfiles.cards.filter((c) => ids.has(c.subjectId));
  const sites = view.file.sites;
  const slot = W / sites.length;
  const items: StageItem[] = [];
  sites.forEach((site, si) => {
    const cx = STAGE.xMin + slot * (si + 0.5);
    const mine = cards.filter((c) => c.siteId === site.id);
    const step = Math.min(0.2, (D - 0.2) / Math.max(1, Math.ceil(mine.length / 4)));
    mine.forEach((c, k) => {
      const col = k % 4;
      const row = Math.floor(k / 4);
      items.push({
        kind: 'figure',
        sex: c.sex,
        x: cx + (col - 1.5) * 0.095,
        y: 0,
        z: STAGE.zMax - 0.12 - row * step,
        h: c.saeLast30Days ? 0.24 : 0.2,
        colour: c.saeLast30Days ? PALETTE.crimson : c.flags.length ? PALETTE.amber : PALETTE.teal,
        tooltip: [
          `${c.subjectId} · ${c.sex === 'F' ? 'female' : 'male'}, ${c.age}`,
          `${c.status}${c.flags.length ? ` · ${c.flags.join(', ')}` : ''}`,
          `${c.visitsCompleted}/${c.visitsDue} visits · ${c.openQueries} open queries`,
          'Select to open the profile',
        ],
        select: { kind: 'drill', path: [`subject:${c.subjectId}`] },
      });
    });
  });
  const recent = items.filter((i) => i.colour === PALETTE.crimson).slice(0, 2);
  const notes: StageNote[] = recent.map((i) =>
    callout(
      i,
      `SAE in the last 30 days: ${i.tooltip[0]?.split(' · ')[0]}`,
      i.tooltip[1] ?? '',
      PALETTE.crimson,
    ),
  );
  return {
    items,
    notes,
    label: {
      title: 'Subjects by site: taller red people had an SAE in the last 30 days',
      legend: [
        { label: 'Unflagged', colour: PALETTE.teal },
        { label: 'Flagged', colour: PALETTE.amber },
        { label: 'SAE in 30 days', colour: PALETTE.crimson },
      ],
      caption: `${cards.length} subjects · select one to open the profile; every mannequin stands in the ward behind this panel`,
      xLabels: sites.map((s, i) => ({ x: STAGE.xMin + slot * (i + 0.5), text: s.city })),
    },
  };
}
